import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store.js';
import { apiGet } from '../api/client.js';

export default function Setup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const setSession = useAuthStore((s) => s.setSession);

  const oauthResult = params.get('oauth');
  const initialStep = oauthResult === 'success' ? 'success' : oauthResult === 'error' ? 'error' : 'welcome';

  const [step, setStep] = useState(initialStep);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(params.get('msg') ? decodeURIComponent(params.get('msg')) : null);

  useEffect(() => {
    if (step === 'success') {
      apiGet('/oauth/client-info')
        .then((data) => setClientInfo(data))
        .catch(() => {});
      if (user) {
        setSession({ user: { ...user, mpConnected: true }, accessToken, refreshToken });
      }
    }
  }, [step]);

  const handleConnect = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { url } = await apiGet('/oauth/start');
      window.location.href = url;
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al iniciar la conexión con MercadoPago');
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 space-y-6 text-center">
          <div className="text-5xl">🎉</div>
          <h1 className="text-2xl font-bold text-white">¡Cuenta conectada!</h1>
          {clientInfo ? (
            <div className="bg-slate-800 rounded-lg p-4 text-left space-y-3 text-sm">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">Negocio</p>
                <p className="text-white font-medium">{clientInfo.business_name}</p>
              </div>
              {clientInfo.mp_user_id && (
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">ID MercadoPago</p>
                  <p className="text-white font-mono">{clientInfo.mp_user_id}</p>
                </div>
              )}
              {clientInfo.email && (
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">Email</p>
                  <p className="text-white">{clientInfo.email}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-800 rounded-lg p-4 animate-pulse h-24" />
          )}
          <p className="text-slate-400 text-sm leading-relaxed">
            A partir de ahora, <strong className="text-white">Mcanet</strong> se encargará de la gestión de tu cuenta en MercadoPago. Podés comenzar a operar desde el panel.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-lg transition-colors"
          >
            Ir al panel
          </button>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 space-y-6 text-center">
          <div className="text-4xl">⚠️</div>
          <h1 className="text-2xl font-bold text-white">Error en la autorización</h1>
          {err && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg px-4 py-3 text-sm text-red-300">
              {err}
            </div>
          )}
          <p className="text-slate-400 text-sm">Podés reintentar la conexión.</p>
          <button
            onClick={() => { setErr(null); setStep('welcome'); }}
            className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 rounded-lg transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="text-4xl mb-2">🔗</div>
          <h1 className="text-2xl font-bold text-white">Conectá tu cuenta de MercadoPago</h1>
          <p className="text-slate-400 text-sm">
            {user?.name ? `Hola ${user.name}. ` : ''}Para comenzar a operar, necesitás autorizar a Mcanet a gestionar tu cuenta en MercadoPago.
          </p>
        </div>

        <div className="space-y-4">
          {[
            'Hacé clic en Conectar con MercadoPago. Serás redirigido al sitio oficial de MP.',
            'Iniciá sesión con tu cuenta de MercadoPago y autorizá el acceso a Mcanet.',
            'Vas a volver automáticamente aquí con la confirmación de la conexión.',
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-3 text-sm text-slate-300">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <p>{text}</p>
            </div>
          ))}
        </div>

        {err && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        <button
          onClick={handleConnect}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors"
        >
          {loading ? 'Redirigiendo a MercadoPago…' : 'Conectar con MercadoPago'}
        </button>
      </div>
    </div>
  );
}
